/**
 * TechTreeModalController - Handles tech tree modal and upgrades
 * Extracted from main.ts for better organization
 */

import { GameState } from '../core/GameState';
import { UIManager } from './UIManager';
import { formatCurrency } from '../utils/Formatters';
import { NewsType } from './TickerManager';
import { 
  TECH_TREE, 
  TechCategory,
  CATEGORY_INFO, 
  getTechsByCategory, 
  getVisibleTechs,
  canUnlockTech,
} from '../data/TechTree';

/** All tech categories in display order */
const TECH_CATEGORIES: TechCategory[] = ['propulsion', 'mining', 'stabilization', 'navigation', 'automation'];

/** Callbacks for tech tree actions */
export interface TechTreeCallbacks {
  onAddNewsItem: (text: string, type: NewsType) => void;
}

/**
 * TechTreeModalController manages the tech tree system
 */
export class TechTreeModalController {
  private gameState: GameState;
  private ui: UIManager;
  private callbacks: TechTreeCallbacks;
  
  constructor(gameState: GameState, ui: UIManager, callbacks: TechTreeCallbacks) {
    this.gameState = gameState;
    this.ui = ui;
    this.callbacks = callbacks;
  }
  
  /**
   * Setup tech tree event listeners
   */
  setup(): void {
    // Update tech tree section visibility based on level
    this.updateSection();

    // Subscribe to state changes to update tech tree
    this.gameState.subscribe('tech-tree', () => this.updateSection());

    // Open tech tree modal button
    this.ui.openTechTreeBtn?.addEventListener('click', () => this.showModal());

    // Close tech tree modal
    document.getElementById('tech-modal-close')?.addEventListener('click', () => this.closeModal());
    this.ui.techTreeModal?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.closeModal();
      }
    });
  }
  
  /**
   * Update the tech tree section in the left panel
   */
  updateSection(): void {
    const lockedEl = this.ui.techTreeLocked;
    const contentEl = this.ui.techTreeContent;
    const statusEl = this.ui.techTreeStatus;
    const effectsEl = this.ui.techEffectsSummary;

    const playerLevel = this.gameState.getPlayerLevel();
    const isUnlocked = playerLevel >= 2;

    if (lockedEl) lockedEl.style.display = isUnlocked ? 'none' : 'block';
    if (contentEl) contentEl.style.display = isUnlocked ? 'block' : 'none';
    if (statusEl) statusEl.textContent = isUnlocked ? '' : '(Level 2+)';

    // Update effects summary in left panel
    if (effectsEl && isUnlocked) {
      const effects = this.gameState.getTechEffects();
      const lines: string[] = [];
      
      if (effects.travelTimeModifier && effects.travelTimeModifier < 1.0) {
        const pct = Math.round((1 - effects.travelTimeModifier) * 100);
        lines.push(`<div class="tech-effect-row"><span>Travel Time:</span><span class="tech-effect-value">-${pct}%</span></div>`);
      }
      if (effects.yieldModifier && effects.yieldModifier > 1.0) {
        const pct = Math.round((effects.yieldModifier - 1) * 100);
        lines.push(`<div class="tech-effect-row"><span>Mining Yield:</span><span class="tech-effect-value">+${pct}%</span></div>`);
      }
      if (effects.anomalyReduction && effects.anomalyReduction > 0) {
        const pct = Math.round(effects.anomalyReduction * 100);
        lines.push(`<div class="tech-effect-row"><span>Anomaly Risk:</span><span class="tech-effect-value">-${pct}%</span></div>`);
      }
      if (effects.crewCostModifier && effects.crewCostModifier < 1.0) {
        const pct = Math.round((1 - effects.crewCostModifier) * 100);
        lines.push(`<div class="tech-effect-row"><span>Crew Costs:</span><span class="tech-effect-value">-${pct}%</span></div>`);
      }
      
      if (lines.length === 0) {
        effectsEl.innerHTML = '<span style="color: #666;">No upgrades yet</span>';
      } else {
        effectsEl.innerHTML = lines.join('');
      }
    }
  }

  /**
   * Show the tech tree modal
   */
  showModal(): void {
    const modal = this.ui.techTreeModal;
    const categoriesEl = this.ui.techCategories;
    const balanceEl = this.ui.techModalBalance;
    const effectsEl = this.ui.techModalEffects;

    if (!modal || !categoriesEl) return;

    // Update balance display
    if (balanceEl) {
      balanceEl.textContent = formatCurrency(this.gameState.data.balance);
    }

    const playerLevel = this.gameState.getPlayerLevel();
    const unlockedTechs = this.gameState.getUnlockedTechs();
    const visibleTechs = getVisibleTechs(playerLevel, unlockedTechs);
    const visibleIds = new Set(visibleTechs.map(t => t.id));

    // Build category columns
    categoriesEl.innerHTML = TECH_CATEGORIES.map(category => {
      const info = CATEGORY_INFO[category];
      const techs = getTechsByCategory(category);
      
      const techHtml = techs.map(tech => {
        const isUnlocked = unlockedTechs.has(tech.id);
        const isVisible = visibleIds.has(tech.id);
        
        if (!isVisible && !isUnlocked) {
          return ''; // Don't show techs that aren't visible yet
        }

        const checkResult = canUnlockTech(tech.id, unlockedTechs, playerLevel, this.gameState.data.balance);
        const canAfford = this.gameState.data.balance >= tech.cost;
        
        let statusClass = 'locked';
        if (isUnlocked) {
          statusClass = 'unlocked';
        } else if (checkResult.canUnlock) {
          statusClass = 'available';
        }

        // Build effect description
        const effectParts: string[] = [];
        if (tech.effects.travelTimeModifier) {
          const pct = Math.round((1 - tech.effects.travelTimeModifier) * 100);
          if (pct > 0) effectParts.push(`${pct}% faster travel`);
        }
        if (tech.effects.yieldModifier) {
          const pct = Math.round((tech.effects.yieldModifier - 1) * 100);
          if (pct > 0) effectParts.push(`${pct}% more yield`);
        }
        if (tech.effects.anomalyReduction) {
          const pct = Math.round(tech.effects.anomalyReduction * 100);
          effectParts.push(`${pct}% safer`);
        }
        if (tech.effects.crewCostModifier) {
          const pct = Math.round((1 - tech.effects.crewCostModifier) * 100);
          if (pct > 0) effectParts.push(`${pct}% crew cost`);
        }

        const effectText = effectParts.length > 0 ? effectParts.join(', ') : 'Base tech';
        const costClass = isUnlocked ? '' : (canAfford ? 'can-afford' : 'cannot-afford');
        const costText = isUnlocked ? '✓ Unlocked' : 
          (tech.cost === 0 ? 'Free' : formatCurrency(tech.cost));
        const reasonText = !isUnlocked && !checkResult.canUnlock && checkResult.reason ? 
          `<div style="font-size: 9px; color: #f88; margin-top: 2px;">${checkResult.reason}</div>` : '';

        return `
          <div class="tech-node ${statusClass}" data-tech-id="${tech.id}" title="${tech.description}">
            <div class="tech-node-name">${tech.name}</div>
            <div class="tech-node-effect">${effectText}</div>
            <div class="tech-node-cost ${costClass}">${costText}</div>
            ${reasonText}
          </div>
        `;
      }).join('');

      return `
        <div class="tech-category">
          <div class="tech-category-header">
            <span class="tech-category-icon">${info.icon}</span>
            <span class="tech-category-name">${info.name}</span>
          </div>
          ${techHtml}
        </div>
      `;
    }).join('');

    // Add click handlers for purchasing
    categoriesEl.querySelectorAll('.tech-node.available').forEach(node => {
      node.addEventListener('click', () => {
        const techId = node.getAttribute('data-tech-id');
        if (techId) {
          this.purchaseTech(techId);
        }
      });
    });

    // Update current effects display
    if (effectsEl) {
      const effects = this.gameState.getTechEffects();
      const lines: string[] = [];
      
      const travelPct = effects.travelTimeModifier ? Math.round((1 - effects.travelTimeModifier) * 100) : 0;
      const yieldPct = effects.yieldModifier ? Math.round((effects.yieldModifier - 1) * 100) : 0;
      const anomalyPct = effects.anomalyReduction ? Math.round(effects.anomalyReduction * 100) : 0;
      const crewPct = effects.crewCostModifier ? Math.round((1 - effects.crewCostModifier) * 100) : 0;

      lines.push(`<div class="tech-effect-row"><span>Travel Time Reduction:</span><span class="tech-effect-value ${travelPct === 0 ? 'neutral' : ''}">${travelPct > 0 ? '-' + travelPct + '%' : 'None'}</span></div>`);
      lines.push(`<div class="tech-effect-row"><span>Mining Yield Bonus:</span><span class="tech-effect-value ${yieldPct === 0 ? 'neutral' : ''}">${yieldPct > 0 ? '+' + yieldPct + '%' : 'None'}</span></div>`);
      lines.push(`<div class="tech-effect-row"><span>Anomaly Risk Reduction:</span><span class="tech-effect-value ${anomalyPct === 0 ? 'neutral' : ''}">${anomalyPct > 0 ? '-' + anomalyPct + '%' : 'None'}</span></div>`);
      lines.push(`<div class="tech-effect-row"><span>Crew Cost Reduction:</span><span class="tech-effect-value ${crewPct === 0 ? 'neutral' : ''}">${crewPct > 0 ? '-' + crewPct + '%' : 'None'}</span></div>`);
      
      effectsEl.innerHTML = lines.join('');
    }

    modal.classList.add('visible');
  }

  /**
   * Close the tech tree modal
   */
  closeModal(): void {
    this.ui.hideModal(this.ui.techTreeModal);
  }

  /**
   * Purchase a tech upgrade
   */
  private purchaseTech(techId: string): void {
    const result = this.gameState.unlockTech(techId);
    
    if (result.success) {
      const tech = TECH_TREE.find(t => t.id === techId);
      this.callbacks.onAddNewsItem(`Tech unlocked: ${tech?.name || techId}!`, 'important');
      // Refresh the modal to show updated state
      this.showModal();
    } else {
      this.callbacks.onAddNewsItem(`Cannot unlock tech: ${result.reason}`, 'critical');
    }
  }
}
